import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  LabeledInput,
  LabeledSelect,
  Stepper,
  Surface,
  Text,
} from "@itwin/itwinui-react";
import { ConsumerDetailsManager } from "../../../components/consumerDetailsManager/consumerDetailsManager";
import { SubscriptionDraftCard } from "../../../components/subscriptionDraftCard/subscriptionDraftCard";
import { SubscriptionDraftForm } from "../../../components/subscriptionDraftForm/subscriptionDraftForm";
import { ProvisioningView } from "../../../components/provisioningView/provisioningView";
import { useGetDiscovery } from "../../../hooks/useGetDiscovery/useGetDiscovery";
import {
  IProvisionableConsumer,
  ISubscriptionDraft,
  createEmptyProvisionableConsumer,
} from "../../../interfaces/IProvisioning";

export const CreateConsumerPage = () => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [consumerId, setConsumerId] = useState<string>();

  // Consumer details state
  const [consumer, setConsumer] = useState<IProvisionableConsumer>(
    createEmptyProvisionableConsumer()
  );
  const [gprId, setGprId] = useState("0");

  // Subscription drafts state
  const [subscriptions, setSubscriptions] = useState<ISubscriptionDraft[]>([]);
  const [editingDraftId, setEditingDraftId] = useState<string | null>(null);

  // Discovery data for producers
  const [discovery, , , getDiscovery] = useGetDiscovery();

  useEffect(() => {
    getDiscovery();
  }, [getDiscovery]);

  // Update GPR ID when it changes
  useEffect(() => {
    setConsumer((prev) => ({
      ...prev,
      serviceGPRId: !isNaN(Number.parseInt(gprId)) ? Number.parseInt(gprId) : 0,
    }));
  }, [gprId]);

  const steps = [
    { name: "Configure" },
    { name: "Provision" },
    { name: "Connection Details" },
  ];

  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Disabled", label: "Disabled" },
  ];

  // Validation
  const canProvision = useMemo(() => {
    if (!consumer.description.trim()) return false;
    if (consumer.serviceGPRId <= 0) return false;
    if (subscriptions.length === 0) return false;
    return true;
  }, [consumer, subscriptions.length]);

  const validationMessages = useMemo(() => {
    const messages: string[] = [];
    if (!consumer.description.trim()) messages.push("Consumer description is required");
    if (consumer.serviceGPRId <= 0) messages.push("Valid Service GPR ID is required");
    if (subscriptions.length === 0) messages.push("At least one subscription is required");
    return messages;
  }, [consumer, subscriptions.length]);

  // Handlers for subscription drafts
  const handleAddSubscription = useCallback((draft: ISubscriptionDraft) => {
    if (editingDraftId) {
      // Updating existing draft
      setSubscriptions((prev) =>
        prev.map((s) => (s.draftId === editingDraftId ? draft : s))
      );
      setEditingDraftId(null);
    } else {
      // Adding new draft
      setSubscriptions((prev) => [...prev, draft]);
    }
  }, [editingDraftId]);

  const handleEditSubscription = useCallback((draftId: string) => {
    setEditingDraftId(draftId);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingDraftId(null);
  }, []);

  const handleDeleteSubscription = useCallback((draftId: string) => {
    setSubscriptions((prev) => prev.filter((s) => s.draftId !== draftId));
    if (editingDraftId === draftId) {
      setEditingDraftId(null);
    }
  }, [editingDraftId]);

  const handleStartProvisioning = useCallback(() => {
    setCurrentStepIndex(1);
  }, []);

  const handleProvisioningComplete = useCallback((newConsumerId: string) => {
    setConsumerId(newConsumerId);
    setCurrentStepIndex(2);
  }, []);

  const handleBackToConfiguration = useCallback(() => {
    setCurrentStepIndex(0);
  }, []);

  const editingDraft = useMemo(
    () => subscriptions.find((s) => s.draftId === editingDraftId),
    [subscriptions, editingDraftId]
  );

  return (
    <div style={{ marginTop: "25px" }}>
      <Stepper steps={steps} currentStep={currentStepIndex} />
      <Surface
        elevation={1}
        style={{
          minHeight: "70vh",
          width: "90%",
          marginLeft: "5%",
          marginRight: "5%",
          marginTop: "25px",
          overflow: "auto",
        }}
      >
        {/* Step 0: Configure Consumer & Subscriptions */}
        {currentStepIndex === 0 && (
          <div style={{ padding: "24px" }}>
            <Flex gap="xl" style={{ minHeight: "60vh" }}>
              {/* Left Panel: Consumer Details & Add Subscription Form */}
              <div style={{ flex: "1 1 65%", paddingRight: "16px", borderRight: "1px solid var(--iui-color-border)" }}>
                <Text variant="headline" as="h2" style={{ marginBottom: "16px" }}>
                  Consumer Details
                </Text>

                <LabeledInput
                  label="Description"
                  value={consumer.description}
                  onChange={(e) =>
                    setConsumer((prev) => ({ ...prev, description: e.target.value }))
                  }
                  style={{ marginBottom: "12px" }}
                  required
                />
                <LabeledInput
                  label="Service GPR ID"
                  value={gprId}
                  onChange={(e) => setGprId(e.target.value.trim())}
                  style={{ marginBottom: "12px" }}
                  required
                />
                <LabeledInput
                  label="Maintainer IDs"
                  placeholder="Comma separated list of emails or client credential IDs"
                  value={consumer.maintainerIds.join(",")}
                  onChange={(e) =>
                    setConsumer((prev) => ({
                      ...prev,
                      maintainerIds: e.target.value
                        ? e.target.value.split(",").map((id) => id.trim())
                        : [],
                    }))
                  }
                  style={{ marginBottom: "12px" }}
                />
                <LabeledSelect
                  label="Status"
                  options={statusOptions}
                  value={consumer.status}
                  onChange={(value) =>
                    setConsumer((prev) => ({ ...prev, status: value }))
                  }
                  style={{ marginBottom: "24px" }}
                />

                <Text variant="headline" as="h3" style={{ marginBottom: "12px", marginTop: "24px" }}>
                  {editingDraftId ? "Edit Subscription" : "Add Subscription"}
                </Text>

                {discovery?.producers && (
                  <SubscriptionDraftForm
                    producers={discovery.producers}
                    existingDraft={editingDraft}
                    onSave={handleAddSubscription}
                    onCancel={editingDraftId ? handleCancelEdit : undefined}
                    isEditing={!!editingDraftId}
                  />
                )}

                {!discovery?.producers && (
                  <Text isMuted>Loading producers...</Text>
                )}
              </div>

              {/* Right Panel: Subscription Drafts List */}
              <div style={{ flex: "1 1 35%", paddingLeft: "16px", minWidth: "280px" }}>
                <Text variant="headline" as="h2" style={{ marginBottom: "16px" }}>
                  Subscriptions ({subscriptions.length})
                </Text>

                {subscriptions.length === 0 ? (
                  <Surface
                    elevation={0}
                    style={{
                      padding: "32px",
                      textAlign: "center",
                      border: "1px dashed var(--iui-color-border)",
                      borderRadius: "8px",
                    }}
                  >
                    <Text isMuted>
                      No subscriptions added yet. Use the form on the left to add
                      subscriptions to producers.
                    </Text>
                  </Surface>
                ) : (
                  <div style={{ maxHeight: "50vh", overflowY: "auto" }}>
                    {subscriptions.map((draft) => (
                      <SubscriptionDraftCard
                        key={draft.draftId}
                        draft={draft}
                        producers={discovery?.producers ?? []}
                        editable={true}
                        onEdit={handleEditSubscription}
                        onDelete={handleDeleteSubscription}
                      />
                    ))}
                  </div>
                )}
              </div>
            </Flex>

            {/* Bottom: Validation & Provision Button */}
            <div
              style={{
                marginTop: "24px",
                paddingTop: "16px",
                borderTop: "1px solid var(--iui-color-border)",
              }}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <div>
                  {validationMessages.length > 0 && (
                    <Text isMuted style={{ fontSize: "0.9em" }}>
                      {validationMessages.join(" • ")}
                    </Text>
                  )}
                </div>
                <Button
                  styleType="high-visibility"
                  onClick={handleStartProvisioning}
                  disabled={!canProvision}
                  size="large"
                >
                  Provision Consumer & Subscriptions
                </Button>
              </Flex>
            </div>
          </div>
        )}

        {/* Step 1: Provisioning */}
        {currentStepIndex === 1 && discovery?.producers && (
          <ProvisioningView
            consumer={consumer}
            subscriptions={subscriptions}
            producers={discovery.producers}
            onComplete={handleProvisioningComplete}
            onBack={handleBackToConfiguration}
          />
        )}

        {/* Step 2: Connection Details */}
        {currentStepIndex === 2 && consumerId && (
          <ConsumerDetailsManager consumerId={consumerId} />
        )}
      </Surface>
    </div>
  );
};
